import type { ConditionalRule } from '@features/service-form/service-form-types';

export class ConditionalFieldManager {
  private form: HTMLFormElement;
  private rules: ConditionalRule[];
  private listeners: Array<{ el: HTMLElement; handler: EventListener }> = [];

  constructor(form: HTMLFormElement, rules: ConditionalRule[]) {
    this.form = form;
    this.rules = rules;
    this.bindListeners();
    this.evaluate();
  }

  private bindListeners(): void {
    const watchedNames = new Set(this.rules.map(r => r.watch));

    watchedNames.forEach(name => {
      const inputs = Array.from(
        this.form.querySelectorAll<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`)
      );

      inputs.forEach(el => {
        const handler: EventListener = () => this.evaluateRule(name);
        el.addEventListener('change', handler);
        this.listeners.push({ el: el as HTMLElement, handler });
      });
    });
  }

  evaluate(): void {
    const watchedNames = new Set(this.rules.map(r => r.watch));
    watchedNames.forEach(name => this.evaluateRule(name));
  }

  private evaluateRule(watchName: string): void {
    const currentValue = this.getFieldValue(watchName);

    this.rules
      .filter(r => r.watch === watchName)
      .forEach(rule => {
        const matches = Array.isArray(rule.equals)
          ? rule.equals.includes(currentValue)
          : rule.equals === currentValue;

        rule.show.forEach(targetId => {
          const target = document.getElementById(targetId);
          if (!target) return;

          if (matches) {
            target.removeAttribute('hidden');
          } else {
            target.setAttribute('hidden', '');
            if (rule.clearOnHide !== false) {
              target
                .querySelectorAll<
                  HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
                >('input, textarea, select')
                .forEach(el => {
                  if (
                    el instanceof HTMLInputElement &&
                    (el.type === 'radio' || el.type === 'checkbox')
                  ) {
                    el.checked = false;
                  } else {
                    el.value = '';
                  }
                  el.dispatchEvent(new Event('change', { bubbles: true }));
                });
            }
          }
        });
      });
  }

  private getFieldValue(name: string): string {
    const radio = this.form.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`);
    if (radio) return radio.value;

    const select = this.form.querySelector<HTMLSelectElement>(`select[name="${name}"]`);
    if (select) return select.value;

    const input = this.form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
    return input?.value ?? '';
  }

  destroy(): void {
    this.listeners.forEach(({ el, handler }) => {
      el.removeEventListener('change', handler);
    });
    this.listeners = [];
  }
}
